import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Check, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import LeadTypePill from "@/components/LeadTypePill";
import { toast } from "sonner";

const DATE_FILTERS = [
    { value: "all", label: "All time" },
    { value: "today", label: "Today" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "60d", label: "Last 60 days" },
    { value: "90d", label: "Last 90 days" },
    { value: "custom", label: "Custom range" },
];

const DAYS = [
    { v: 0, l: "Mon" }, { v: 1, l: "Tue" }, { v: 2, l: "Wed" },
    { v: 3, l: "Thu" }, { v: 4, l: "Fri" }, { v: 5, l: "Sat" }, { v: 6, l: "Sun" },
];

export default function CampaignCreate() {
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [channel, setChannel] = useState("sms");
    const [smsTemplate, setSmsTemplate] = useState("");
    const [emailTemplate, setEmailTemplate] = useState("");
    const [includeTypes, setIncludeTypes] = useState([]);
    const [dateKind, setDateKind] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [sendRate, setSendRate] = useState(250);
    const [testBatchSize, setTestBatchSize] = useState(3);
    const [windowMode, setWindowMode] = useState("global");
    const [winDays, setWinDays] = useState([0, 1, 2, 3, 4]);
    const [winStart, setWinStart] = useState("09:00");
    const [winEnd, setWinEnd] = useState("17:00");
    const [types, setTypes] = useState([]);
    const [settings, setSettings] = useState({ sms_templates: [], email_templates: [], operating_hours: null });
    const [smsTemplates, setSmsTemplates] = useState([]);   // from /api/message-templates (channel=sms, active)
    const [emailTemplates, setEmailTemplates] = useState([]); // channel=email
    const [smsPreview, setSmsPreview] = useState(null);
    const [estimating, setEstimating] = useState(false);
    const [estimate, setEstimate] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/lead-types"),
            api.get("/settings"),
            api.get("/message-templates", { params: { channel: "sms", active: true } }),
            api.get("/message-templates", { params: { channel: "email", active: true } }),
        ])
            .then(([t, s, sms, em]) => {
                setTypes(t.data);
                setSettings(s.data);
                setSmsTemplates(sms.data);
                setEmailTemplates(em.data);
            });
    }, []);

    // Live preview of the selected SMS template — uses the first lead from the
    // currently-included lead types so the operator sees real personalization
    // instead of a synthetic "Hi Alex".
    useEffect(() => {
        if (!smsTemplate) { setSmsPreview(null); return; }
        let alive = true;
        const run = async () => {
            // Pick a sample lead from the included lead types
            let sampleLeadId = null;
            if (includeTypes.length) {
                try {
                    const { data } = await api.get("/leads", {
                        params: { page_size: 1, lead_type_id: includeTypes[0] },
                    });
                    sampleLeadId = data?.items?.[0]?.id || null;
                } catch { /* ignore */ }
            }
            const { data: prev } = await api.post(`/message-templates/${smsTemplate}/preview`, {
                lead_id: sampleLeadId,
                variables: { campaign_name: name || "(unnamed campaign)" },
            });
            if (alive) setSmsPreview(prev);
        };
        run().catch(() => {});
        return () => { alive = false; };
    }, [smsTemplate, name, includeTypes]);

    const toggleType = (id) => {
        setIncludeTypes(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    const toggleDay = (d) => {
        setWinDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
    };

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (includeTypes.length === 0) { setEstimate(null); return; }
            setEstimating(true);
            try {
                let total = 0;
                for (const tid of includeTypes) {
                    const { data } = await api.get("/leads", { params: { lead_type_id: tid, page_size: 1 } });
                    total += data.total;
                }
                if (!cancelled) setEstimate(total);
            } finally {
                if (!cancelled) setEstimating(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [includeTypes]);

    const submit = async () => {
        if (!name.trim()) return toast.error("Name is required");
        if (includeTypes.length === 0) return toast.error("Pick at least one lead type to include");
        const send_window = windowMode === "custom"
            ? { mode: "custom", days: winDays, start: winStart, end: winEnd }
            : { mode: windowMode };
        try {
            const { data } = await api.post("/campaigns", {
                name: name.trim(),
                channel,
                sms_template_id: smsTemplate || null,
                email_template_id: emailTemplate || null,
                lead_type_ids: includeTypes,
                exclude_lead_type_ids: [],
                date_filter_kind: dateKind,
                date_filter_from: dateKind === "custom" ? (dateFrom || null) : null,
                date_filter_to: dateKind === "custom" ? (dateTo || null) : null,
                send_rate_per_hour: Number(sendRate) || 250,
                send_window,
                test_batch_size: Math.max(1, Math.min(50, Number(testBatchSize) || 3)),
            });
            toast.success("Campaign created as draft");
            navigate(`/campaigns/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const oh = settings.operating_hours;
    const globalLabel = oh
        ? `${(oh.days || []).map(d => DAYS[d]?.l).join(", ")} · ${oh.start}–${oh.end} · ${oh.timezone}`
        : "Mon–Fri · 09:00–17:00";

    const eta = (() => {
        if (!estimate || !sendRate) return null;
        const hours = estimate / Number(sendRate);
        const h = Math.floor(hours), m = Math.round((hours - h) * 60);
        if (h === 0 && m === 0) return "<1m";
        return h === 0 ? `${m}m` : `${h}h ${m}m`;
    })();

    return (
        <div className="space-y-6 max-w-2xl" data-testid="campaign-create-page">
            <Link to="/campaigns" className="inline-flex items-center text-sm text-muted-foreground hover:text-gray-900">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to campaigns
            </Link>
            <div>
                <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">New Campaign</h1>
                <p className="text-sm text-muted-foreground mt-1">Configure once, start when you're ready.</p>
            </div>

            <div className="bg-white border border-border rounded-xl p-6 shadow-sm space-y-6">
                <div>
                    <Label>Campaign name</Label>
                    <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. SAVE Program Alert" data-testid="campaign-name-input" />
                </div>

                <div>
                    <Label>Communication type</Label>
                    <Select value={channel} onValueChange={setChannel}>
                        <SelectTrigger data-testid="campaign-channel-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="both">SMS + Email</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div>
                    <Label className="mb-2 block">Include lead types</Label>
                    <p className="text-xs text-muted-foreground mb-3">Leads belonging to any selected type are targeted.</p>
                    <div className="flex flex-wrap gap-2" data-testid="lead-type-chooser">
                        {types.map(t => {
                            const active = includeTypes.includes(t.id);
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => toggleType(t.id)}
                                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm transition-all ${active ? "border-primary bg-primary/5 text-primary" : "border-border bg-white text-gray-700 hover:bg-gray-50"}`}
                                    data-testid={`select-type-${t.id}`}
                                >
                                    {active && <Check className="h-3.5 w-3.5" />}
                                    <LeadTypePill leadType={t} />
                                    <span className="text-xs text-muted-foreground">{t.lead_count}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-4 border border-dashed border-border rounded-md px-3 py-2 text-xs text-muted-foreground inline-flex items-center gap-2" data-testid="exclude-future">
                        <Info className="h-3.5 w-3.5" /> Exclude lead types — coming soon.
                    </div>
                </div>

                <div>
                    <Label>Filter by import date</Label>
                    <Select value={dateKind} onValueChange={setDateKind}>
                        <SelectTrigger data-testid="date-filter-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {DATE_FILTERS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    {dateKind === "custom" && (
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <div><Label>From</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="date-from-input" /></div>
                            <div><Label>To</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="date-to-input" /></div>
                        </div>
                    )}
                </div>

                <div>
                    <Label className="mb-2 block">Send window</Label>
                    <div className="space-y-2" data-testid="send-window-chooser">
                        {[
                            { v: "global", l: "Use Global Operating Hours", sub: globalLabel },
                            { v: "24h", l: "24 hours", sub: "Send anytime, day or night." },
                            { v: "custom", l: "Custom hours", sub: "Choose days and times below." },
                        ].map(opt => (
                            <label key={opt.v} className={`flex items-start gap-3 px-3 py-2.5 rounded-md border cursor-pointer ${windowMode === opt.v ? "border-primary bg-primary/5" : "border-border"}`}>
                                <input
                                    type="radio"
                                    name="window-mode"
                                    checked={windowMode === opt.v}
                                    onChange={() => setWindowMode(opt.v)}
                                    className="mt-1 accent-[hsl(var(--primary))]"
                                    data-testid={`window-mode-${opt.v}`}
                                />
                                <div className="flex-1">
                                    <div className="text-sm text-gray-900 font-medium">{opt.l}</div>
                                    <div className="text-xs text-muted-foreground">{opt.sub}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                    {windowMode === "custom" && (
                        <div className="mt-4 border border-border rounded-md p-4 space-y-3 bg-gray-50/40">
                            <div>
                                <Label className="mb-2 block">Days</Label>
                                <div className="flex flex-wrap gap-1.5">
                                    {DAYS.map(d => (
                                        <button
                                            key={d.v}
                                            type="button"
                                            onClick={() => toggleDay(d.v)}
                                            className={`px-2.5 py-1 rounded-md text-xs border ${winDays.includes(d.v) ? "border-primary bg-primary/10 text-primary" : "border-border bg-white text-gray-700 hover:bg-gray-50"}`}
                                            data-testid={`day-${d.v}`}
                                        >
                                            {d.l}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Start</Label><Input type="time" value={winStart} onChange={e => setWinStart(e.target.value)} data-testid="window-start" /></div>
                                <div><Label>End</Label><Input type="time" value={winEnd} onChange={e => setWinEnd(e.target.value)} data-testid="window-end" /></div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(channel === "sms" || channel === "both") && (
                        <div>
                            <div className="flex items-center justify-between">
                                <Label>SMS Template <span className="text-rose-600">*</span></Label>
                                <Link to="/templates" className="text-xs text-primary hover:underline" data-testid="manage-templates-link">Manage</Link>
                            </div>
                            <Select value={smsTemplate} onValueChange={setSmsTemplate}>
                                <SelectTrigger data-testid="sms-template-select"><SelectValue placeholder="Pick template" /></SelectTrigger>
                                <SelectContent>
                                    {smsTemplates.length === 0 ? (
                                        <div className="px-3 py-2 text-xs text-muted-foreground">No active SMS templates. Create one on the Templates page.</div>
                                    ) : smsTemplates.map(t => (
                                        <SelectItem key={t.id} value={t.id}>
                                            <span className="font-mono text-[11px] text-muted-foreground mr-1">{t.template_key}</span>
                                            {t.template_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-muted-foreground mt-1">Required to start a live SMS campaign.</p>
                        </div>
                    )}
                    {(channel === "email" || channel === "both") && (
                        <div>
                            <Label>Email Template {(channel === "email" || channel === "both") && <span className="text-rose-600">*</span>}</Label>
                            <Select value={emailTemplate} onValueChange={setEmailTemplate}>
                                <SelectTrigger data-testid="email-template-select"><SelectValue placeholder="Pick template" /></SelectTrigger>
                                <SelectContent>
                                    {emailTemplates.length === 0 ? (
                                        <div className="px-3 py-2 text-xs text-muted-foreground">No active email templates.</div>
                                    ) : emailTemplates.map(t => (
                                        <SelectItem key={t.id} value={t.id}>
                                            <span className="font-mono text-[11px] text-muted-foreground mr-1">{t.template_key}</span>
                                            {t.template_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {emailTemplate && (() => {
                                const sel = emailTemplates.find(t => t.id === emailTemplate);
                                if (!sel) return null;
                                const placeholder = !sel.sendgrid_template_id ||
                                    !sel.sendgrid_template_id.startsWith("d-") ||
                                    sel.sendgrid_template_id.toUpperCase().includes("DEMO") ||
                                    sel.sendgrid_template_id.toUpperCase().includes("REPLACE-ME");
                                return (
                                    <div className="mt-2 bg-slate-50 border border-border rounded-md p-3 text-xs space-y-1.5" data-testid="email-template-debug">
                                        <div className="flex justify-between gap-2">
                                            <span className="text-muted-foreground">SendGrid Template ID</span>
                                            <code className={`font-mono ${placeholder ? "text-rose-700" : "text-gray-900"}`}>{sel.sendgrid_template_id || "—"}</code>
                                        </div>
                                        <div className="flex justify-between gap-2">
                                            <span className="text-muted-foreground">Variables passed</span>
                                            <code className="font-mono text-gray-900">{`{first_name} {last_name} {email} {phone} {campaign_name}`}</code>
                                        </div>
                                        {placeholder && (
                                            <div className="text-rose-700 pt-1">Placeholder/missing ID — live email send will be blocked.</div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>

                {smsPreview && (channel === "sms" || channel === "both") && (
                    <div className="bg-slate-50 border border-border rounded-md p-3" data-testid="sms-preview">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center justify-between gap-2">
                            <span className="flex items-center gap-2">
                                <span>SMS preview</span>
                                {smsPreview.sample_source?.kind === "lead" ? (
                                    <span className="text-[10px] normal-case tracking-normal bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">
                                        sample: {smsPreview.sample_source.name || "real lead"}
                                    </span>
                                ) : (
                                    <span className="text-[10px] normal-case tracking-normal bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
                                        synthetic sample — pick lead types to preview with a real lead
                                    </span>
                                )}
                            </span>
                            <code className="font-mono text-[10px]">{smsPreview.template_key}</code>
                        </div>
                        <pre className="text-sm text-gray-900 whitespace-pre-wrap break-words font-mono">{smsPreview.body}</pre>
                    </div>
                )}

                <div>
                    <Label>Send rate (per hour)</Label>
                    <Input type="number" value={sendRate} onChange={e => setSendRate(e.target.value)} data-testid="send-rate-input" />
                </div>

                <div>
                    <Label>Test batch size</Label>
                    <Input
                        type="number"
                        min={1}
                        max={50}
                        value={testBatchSize}
                        onChange={e => setTestBatchSize(e.target.value)}
                        data-testid="test-batch-size-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                        Number of real targeted leads sent when you click "Run test batch" on the campaign. Capped at 50.
                    </p>
                </div>

                <div className="bg-gray-50/60 border border-border rounded-md px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3" data-testid="target-estimate">
                    <div>
                        <span className="text-muted-foreground">Estimated reach:</span>{" "}
                        <span className="font-medium text-gray-900 tabular">
                            {includeTypes.length === 0 ? "—" : estimating ? "calculating…" : (estimate ?? 0).toLocaleString()}
                        </span>
                    </div>
                    <div>
                        <span className="text-muted-foreground">Est. completion:</span>{" "}
                        <span className="font-medium text-gray-900 tabular">{eta || "—"}</span>
                    </div>
                </div>

                <div className="flex gap-2 pt-2">
                    <Button onClick={submit} data-testid="create-campaign-submit">Create as draft</Button>
                    <Button variant="outline" onClick={() => navigate("/campaigns")}>Cancel</Button>
                </div>
            </div>
        </div>
    );
}
